/**
 * Admin Contract Template Editor — page `/admin/modele-contrat`.
 *
 * Two axes of customisation:
 *   • **Scope** — édite soit le modèle maître (utilisé par tous les
 *     rôles qui n'ont pas leur variante), soit un modèle spécifique
 *     à un rôle (RD/RE/CD). Le sélecteur en haut de page bascule entre
 *     scopes.
 *   • **Historique (5 dernières versions)** — chaque sauvegarde
 *     conserve la version précédente ; l'admin peut revenir en arrière
 *     en 1 clic (utile après un mauvais copier-coller).
 *
 * Chaque sauvegarde est validée côté backend contre 3 fixtures
 * (RD/RE/CD × M/F × IdF/PACA) — un template cassé est REJETÉ avec un
 * message d'erreur clair, la version en place n'est pas remplacée.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
    AlertTriangle,
    CheckCircle2,
    Clock,
    Eye,
    FileText,
    GitCompareArrows,
    History,
    Info,
    Loader2,
    RefreshCw,
    Save,
    ShieldCheck,
    Undo2,
    X,
} from "lucide-react";

import { api } from "../lib/api";


const SCOPES = [
    { key: "master", label: "Modèle maître", hint: "Utilisé par tous les rôles qui n'ont pas leur propre variante" },
    { key: "role:recruteur_donateurs", label: "Recruteur de donateurs", hint: "Variante spécifique au rôle RD (écrase le maître pour ce rôle)" },
    { key: "role:responsable_equipe", label: "Responsable d'équipe", hint: "Variante spécifique au rôle RE (écrase le maître pour ce rôle)" },
    { key: "role:charge_developpement", label: "Chargé de développement", hint: "Variante spécifique au rôle CD (écrase le maître pour ce rôle)" },
];

const SCENARIOS = [
    { key: "re_f_paca", label: "RE Femme PACA", hint: "Accords féminins, salaire mensuel, PACA" },
    { key: "rd_m_idf", label: "RD Homme IdF", hint: "Taux horaire, baseline" },
    { key: "cd_f_idf", label: "CD Femme IdF", hint: "Missions élargies, féminin" },
];

const CHEATSHEET = [
    { group: "Identité", items: [
        ["{{ civilite }}", "Monsieur / Madame"],
        ["{{ lastname }}", "MARTIN"],
        ["{{ firstnames }}", "Camille"],
        ["{{ fullname }}", "MARTIN Camille"],
        ["{{ address }}", "12 Rue Paradis, 13001 Marseille"],
        ["{{ birth_date_fr }}", "15 avril 1988"],
        ["{{ ssn }}", "2 88 04 13 001 001 88"],
    ]},
    { group: "Genre (auto)", items: [
        ["{{ salarie_lc }}", "le salarié / la salariée"],
        ["{{ salarie_cap }}", "Le salarié / La salariée"],
        ["{{ engage }}", "engagé / engagée"],
        ["{{ pronom_maj }}", "Il / Elle"],
        ["{{ pronom_lc }}", "il / elle"],
        ["{{ soumis }}", "soumis / soumise"],
        ["{{ rattache }}", "rattaché / rattachée"],
        ["{{ e_suffix }}", "'' ou 'e'"],
    ]},
    { group: "Rôle & région", items: [
        ["{{ role_label }}", "Chargé de Développement Terrain"],
        ["{{ region_label }}", "Provence-Alpes-Côte d'Azur"],
        ["{% if is_re %}…{% endif %}", "Bloc Responsable d'équipe"],
        ["{% if is_cd %}…{% endif %}", "Bloc Chargé de développement"],
        ["{% if is_full_time %}…{% endif %}", "Bloc temps plein"],
    ]},
    { group: "Dates & horaires", items: [
        ["{{ start_date_fr }}", "1er août 2026"],
        ["{{ end_date_fr }}", "31 août 2026"],
        ["{{ trial_days }}", "4"],
        ["{{ start_hour }}", "10"],
    ]},
    { group: "Rémunération", items: [
        ["{{ hourly_rate_str }}", "12,50"],
        ["{{ monthly_salary_str }}", "2 578,39"],
        ["{{ monthly_salary_words }}", "deux mille cinq cent…"],
    ]},
];


function formatDateTime(iso) {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" }); }
    catch { return iso; }
}


export default function ContractTemplateAdmin() {
    const [scope, setScope] = useState("master");
    const [scopesSummary, setScopesSummary] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [resetting, setResetting] = useState(false);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [rollbackBusy, setRollbackBusy] = useState(false);
    const [html, setHtml] = useState("");
    const [pristine, setPristine] = useState("");
    const [meta, setMeta] = useState(null);
    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewHtml, setPreviewHtml] = useState("");
    const [previewScenario, setPreviewScenario] = useState("re_f_paca");
    const [showCheatsheet, setShowCheatsheet] = useState(true);
    const [showResetConfirm, setShowResetConfirm] = useState(false);
    const [rollbackTarget, setRollbackTarget] = useState(null);
    // Diff modal state — `diffTarget` is the historical version to compare
    // AGAINST the current version. `diffData` holds the structured
    // response from the backend (chunks + stats).
    const [diffTarget, setDiffTarget] = useState(null);
    const [diffData, setDiffData] = useState(null);
    const [diffLoading, setDiffLoading] = useState(false);

    const dirty = html !== pristine;

    const loadScopesSummary = useCallback(async () => {
        try {
            const { data } = await api.get("/contracts/template/scopes");
            setScopesSummary(data.scopes || []);
        } catch {
            // non-critical — the top switcher just shows labels without badges
        }
    }, []);

    const load = useCallback(async (nextScope = scope) => {
        setLoading(true);
        try {
            const { data } = await api.get("/contracts/template", { params: { scope: nextScope } });
            setHtml(data.html || "");
            setPristine(data.html || "");
            setMeta({
                scope: data.scope,
                scope_label: data.scope_label,
                source: data.source,
                version: data.version,
                updated_at: data.updated_at,
                updated_by_name: data.updated_by_name,
                history: data.history || [],
            });
        } catch (e) {
            toast.error("Impossible de charger le modèle de contrat");
        } finally {
            setLoading(false);
        }
    }, [scope]);

    useEffect(() => { load(); loadScopesSummary(); /* eslint-disable-next-line */ }, [scope]);

    async function handleSave() {
        setSaving(true);
        try {
            const { data } = await api.put("/contracts/template", { html, scope });
            setPristine(html);
            setMeta({
                scope: data.scope, scope_label: data.scope_label,
                source: data.source, version: data.version,
                updated_at: data.updated_at, updated_by_name: data.updated_by_name,
                history: data.history || [],
            });
            await loadScopesSummary();
            toast.success(`Modèle "${data.scope_label}" sauvegardé (version ${data.version}).`);
        } catch (e) {
            const msg = e?.response?.data?.detail || "La sauvegarde a échoué";
            toast.error(msg, { duration: 12000 });
        } finally {
            setSaving(false);
        }
    }

    async function handleReset() {
        setResetting(true);
        try {
            await api.delete("/contracts/template", { params: { scope } });
            await load();
            await loadScopesSummary();
            toast.success("Modèle par défaut restauré.");
            setShowResetConfirm(false);
        } catch (e) {
            toast.error("Restauration échouée");
        } finally {
            setResetting(false);
        }
    }

    async function handlePreview() {
        setPreviewLoading(true);
        try {
            const { data } = await api.post("/contracts/template/preview", {
                html, scenario: previewScenario, scope,
            });
            setPreviewHtml(data.html);
            setPreviewOpen(true);
        } catch (e) {
            const msg = e?.response?.data?.detail || "L'aperçu a échoué";
            toast.error(msg, { duration: 12000 });
        } finally {
            setPreviewLoading(false);
        }
    }

    async function handlePreviewVersion(version) {
        setPreviewLoading(true);
        try {
            const { data } = await api.get("/contracts/template/version", { params: { scope, version } });
            // Render this old HTML against the current scenario
            const preview = await api.post("/contracts/template/preview", {
                html: data.html, scenario: previewScenario, scope,
            });
            setPreviewHtml(preview.data.html);
            setPreviewOpen(true);
        } catch (e) {
            toast.error("Impossible de prévisualiser cette version");
        } finally {
            setPreviewLoading(false);
        }
    }

    async function handleRollback(version) {
        setRollbackBusy(true);
        try {
            const { data } = await api.post("/contracts/template/rollback", { scope, version });
            setMeta({
                scope: data.scope, scope_label: data.scope_label,
                source: data.source, version: data.version,
                updated_at: data.updated_at, updated_by_name: data.updated_by_name,
                history: data.history || [],
            });
            await load();
            await loadScopesSummary();
            toast.success(`Version ${version} restaurée (nouveau v${data.version}).`);
            setRollbackTarget(null);
        } catch (e) {
            const msg = e?.response?.data?.detail || "Le rollback a échoué";
            toast.error(msg, { duration: 10000 });
        } finally {
            setRollbackBusy(false);
        }
    }

    async function handleShowDiff(version) {
        // Compare THIS historical version → current (source of truth
        // right now). CEO's most common audit question: "qu'est-ce que
        // j'ai changé depuis v3 ?" — this shows exactly that.
        setDiffTarget(version);
        setDiffData(null);
        setDiffLoading(true);
        try {
            const { data } = await api.get("/contracts/template/diff", {
                params: { scope, from_version: version, to_version: meta.version },
            });
            setDiffData(data);
        } catch (e) {
            const msg = e?.response?.data?.detail || "Le diff a échoué";
            toast.error(msg, { duration: 10000 });
            setDiffTarget(null);
        } finally {
            setDiffLoading(false);
        }
    }

    const bytes = useMemo(() => new Blob([html]).size, [html]);
    const lines = useMemo(() => (html.match(/\n/g) || []).length + 1, [html]);
    const currentScope = SCOPES.find(s => s.key === scope);

    return (
        <div className="min-h-screen bg-shariti-cream">
            {/* Header */}
            <div className="px-4 pt-6 pb-4 border-b border-shariti-border bg-white">
                <div className="max-w-7xl mx-auto space-y-4">
                    <div>
                        <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1 inline-flex items-center gap-1.5">
                            <FileText className="w-3 h-3" /> Administration · Modèle contrat
                        </div>
                        <h1 className="text-2xl font-black text-shariti-black">
                            Éditeur du contrat de travail
                        </h1>
                        <p className="text-sm text-slate-600 mt-1 max-w-3xl">
                            Modifie le modèle maître (utilisé par tous les rôles) OU une variante spécifique
                            par rôle. Les 5 dernières versions sont conservées — un rollback restaure une
                            version antérieure en 1 clic.
                        </p>
                    </div>
                    {/* Scope tabs */}
                    <div className="flex flex-wrap gap-2" data-testid="contract-template-scope-tabs">
                        {SCOPES.map(s => {
                            const summary = scopesSummary.find(x => x.scope === s.key);
                            const isCustom = summary?.source === "custom";
                            const isActive = scope === s.key;
                            return (
                                <button
                                    key={s.key}
                                    onClick={() => setScope(s.key)}
                                    data-testid={`contract-template-scope-${s.key}`}
                                    className={`px-3 py-2 rounded-xl border-2 text-left transition min-w-[190px] ${
                                        isActive
                                            ? "border-shariti-orange bg-shariti-orange-soft/30"
                                            : "border-shariti-border bg-white hover:border-shariti-orange"
                                    }`}
                                    title={s.hint}
                                >
                                    <div className="flex items-center gap-2">
                                        <div className={`text-[11px] font-black uppercase tracking-widest ${isActive ? "text-shariti-orange" : "text-shariti-black"}`}>
                                            {s.label}
                                        </div>
                                        {isCustom && (
                                            <span className="ml-auto text-[9px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800">
                                                v{summary.version}
                                            </span>
                                        )}
                                    </div>
                                    <div className={`text-[10px] mt-0.5 ${isCustom ? "text-emerald-700" : "text-slate-500"}`}>
                                        {isCustom ? `Personnalisé — ${formatDateTime(summary.updated_at)}` : "Modèle d'usine"}
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                </div>
            </div>

            {/* Body */}
            <div className="px-4 py-6">
                <div className="max-w-7xl mx-auto grid gap-6" style={{ gridTemplateColumns: showCheatsheet ? "minmax(0,1fr) 320px" : "minmax(0,1fr)" }}>
                    {/* Editor column */}
                    <div className="space-y-3 min-w-0">
                        <div className="flex items-start justify-between flex-wrap gap-2">
                            <div className="min-w-0">
                                <div className="text-[11px] font-black uppercase tracking-widest text-slate-500 inline-flex items-center gap-2 flex-wrap">
                                    <span>Source Jinja · HTML — {currentScope?.label}</span>
                                    <span className="text-slate-400 font-normal normal-case tracking-normal">
                                        {lines.toLocaleString("fr-FR")} lignes · {(bytes / 1024).toFixed(1)} Ko
                                    </span>
                                    {dirty && (
                                        <span className="px-1.5 py-0.5 rounded-md bg-amber-100 text-amber-800 text-[10px]">
                                            Modifications non sauvegardées
                                        </span>
                                    )}
                                </div>
                                {meta?.source === "custom" && (
                                    <div className="text-[11px] text-slate-500 mt-1">
                                        Version {meta.version} · Modifié par <strong>{meta.updated_by_name}</strong> le {formatDateTime(meta.updated_at)}
                                    </div>
                                )}
                            </div>
                            <div className="flex items-center gap-2 flex-wrap">
                                <select
                                    value={previewScenario}
                                    onChange={(e) => setPreviewScenario(e.target.value)}
                                    data-testid="contract-template-scenario"
                                    className="input-shariti text-xs h-8 max-w-[170px]"
                                    title="Profil de test pour l'aperçu"
                                >
                                    {SCENARIOS.map(s => (
                                        <option key={s.key} value={s.key}>{s.label}</option>
                                    ))}
                                </select>
                                <button
                                    onClick={handlePreview}
                                    disabled={previewLoading || loading}
                                    className="btn-shariti-outline text-xs"
                                    data-testid="contract-template-preview"
                                >
                                    {previewLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Eye className="w-3.5 h-3.5" />}
                                    Aperçu
                                </button>
                                <button
                                    onClick={() => setShowResetConfirm(true)}
                                    disabled={loading || resetting || meta?.source !== "custom"}
                                    className="btn-shariti-outline text-xs disabled:opacity-40 disabled:cursor-not-allowed"
                                    data-testid="contract-template-reset"
                                    title={meta?.source !== "custom" ? "Aucune version personnalisée à restaurer" : "Restaurer le modèle d'usine"}
                                >
                                    <Undo2 className="w-3.5 h-3.5" />
                                    Modèle d&apos;usine
                                </button>
                                <button
                                    onClick={handleSave}
                                    disabled={saving || loading || !dirty}
                                    className="btn-shariti text-xs disabled:opacity-40 disabled:cursor-not-allowed"
                                    data-testid="contract-template-save"
                                >
                                    {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                                    Enregistrer
                                </button>
                            </div>
                        </div>
                        {loading ? (
                            <div className="h-96 flex items-center justify-center rounded-xl bg-white border-2 border-shariti-border">
                                <Loader2 className="w-6 h-6 animate-spin text-shariti-orange" />
                            </div>
                        ) : (
                            <textarea
                                value={html}
                                onChange={(e) => setHtml(e.target.value)}
                                data-testid="contract-template-textarea"
                                spellCheck={false}
                                className="w-full font-mono text-xs leading-relaxed bg-white border-2 border-shariti-border rounded-xl p-4 focus:outline-none focus:ring-2 focus:ring-shariti-orange focus:border-shariti-orange resize-vertical"
                                style={{ minHeight: "60vh", tabSize: 2 }}
                                placeholder="Chargement…"
                            />
                        )}

                        {/* Version history */}
                        {meta?.source === "custom" && meta.history && meta.history.length > 0 && (
                            <div className="bg-white border-2 border-shariti-border rounded-xl p-4" data-testid="contract-template-history">
                                <div className="flex items-center gap-2 mb-3">
                                    <History className="w-4 h-4 text-shariti-orange" />
                                    <div className="text-[11px] font-black uppercase tracking-widest text-shariti-black">
                                        Historique — 5 dernières versions
                                    </div>
                                </div>
                                <ul className="divide-y divide-shariti-border">
                                    {meta.history.map((v) => (
                                        <li
                                            key={v.version}
                                            className="py-2 flex items-center justify-between gap-3 flex-wrap"
                                            data-testid={`contract-template-history-v${v.version}`}
                                        >
                                            <div className="min-w-0">
                                                <div className="text-xs font-black text-shariti-black">
                                                    Version {v.version}
                                                </div>
                                                <div className="text-[11px] text-slate-500">
                                                    <Clock className="w-3 h-3 inline mr-1" />
                                                    {formatDateTime(v.updated_at)} · {v.updated_by_name || "—"} · {((v.bytes || 0) / 1024).toFixed(1)} Ko
                                                </div>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <button
                                                    onClick={() => handleShowDiff(v.version)}
                                                    disabled={diffLoading}
                                                    className="btn-shariti-outline text-[11px] px-2 py-1"
                                                    data-testid={`contract-template-diff-v${v.version}`}
                                                    title={`Voir les modifications entre v${v.version} et v${meta.version} (actuelle)`}
                                                >
                                                    <GitCompareArrows className="w-3 h-3" /> Diff
                                                </button>
                                                <button
                                                    onClick={() => handlePreviewVersion(v.version)}
                                                    disabled={previewLoading}
                                                    className="btn-shariti-outline text-[11px] px-2 py-1"
                                                    data-testid={`contract-template-preview-v${v.version}`}
                                                >
                                                    <Eye className="w-3 h-3" /> Aperçu
                                                </button>
                                                <button
                                                    onClick={() => setRollbackTarget(v)}
                                                    className="btn-shariti-outline text-[11px] px-2 py-1"
                                                    data-testid={`contract-template-rollback-v${v.version}`}
                                                >
                                                    <RefreshCw className="w-3 h-3" /> Restaurer
                                                </button>
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                                <div className="mt-2 text-[10px] text-slate-500 italic">
                                    Un rollback crée une <strong>nouvelle version</strong> (v{meta.version + 1})
                                    — la version actuelle est archivée dans l&apos;historique.
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Cheatsheet column */}
                    {showCheatsheet && (
                        <aside className="space-y-3 min-w-0">
                            <div className="flex items-center justify-between">
                                <div className="text-[11px] font-black uppercase tracking-widest text-shariti-orange">
                                    Variables disponibles
                                </div>
                                <button
                                    onClick={() => setShowCheatsheet(false)}
                                    className="text-slate-400 hover:text-shariti-orange p-1"
                                    aria-label="Masquer les variables"
                                >
                                    <X className="w-3.5 h-3.5" />
                                </button>
                            </div>
                            <div className="bg-white border-2 border-shariti-border rounded-xl p-4">
                                <div className="space-y-3 text-[11px] max-h-[65vh] overflow-auto pr-1">
                                    {CHEATSHEET.map(group => (
                                        <div key={group.group}>
                                            <div className="font-black text-shariti-black mb-1">
                                                {group.group}
                                            </div>
                                            <ul className="space-y-1">
                                                {group.items.map(([tag, ex]) => (
                                                    <li key={tag} className="flex flex-col">
                                                        <code className="bg-shariti-orange-soft/40 text-shariti-orange px-1 rounded font-mono text-[10px] w-fit">
                                                            {tag}
                                                        </code>
                                                        <span className="text-slate-500 text-[10px] italic ml-1">→ {ex}</span>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    ))}
                                </div>
                            </div>
                            <div className="bg-amber-50 border-2 border-amber-200 rounded-xl p-3 text-[11px] text-amber-900 space-y-1">
                                <div className="font-black flex items-center gap-1">
                                    <AlertTriangle className="w-3 h-3" /> Bonnes pratiques
                                </div>
                                <p>Ne renomme jamais une variable — préviens le dev d&apos;abord.</p>
                                <p>Utilise <code>{'{% if is_cd %}…{% endif %}'}</code> pour cibler un rôle sans casser les autres.</p>
                                <p>Prévisualise avant de sauvegarder.</p>
                                <p>Une variante par rôle <strong>écrase</strong> le maître pour ce rôle uniquement.</p>
                            </div>
                        </aside>
                    )}
                    {!showCheatsheet && (
                        <button
                            onClick={() => setShowCheatsheet(true)}
                            className="fixed bottom-6 right-6 bg-shariti-orange text-white rounded-full p-3 shadow-lg hover:scale-105 transition"
                            aria-label="Afficher les variables"
                            data-testid="contract-template-toggle-cheatsheet"
                        >
                            <Info className="w-5 h-5" />
                        </button>
                    )}
                </div>
            </div>

            {/* Preview modal */}
            {previewOpen && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
                    onClick={() => setPreviewOpen(false)}
                    data-testid="contract-template-preview-modal"
                >
                    <div
                        className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl h-[85vh] flex flex-col overflow-hidden"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="px-5 py-3 border-b border-shariti-border flex items-center justify-between bg-shariti-cream">
                            <div className="min-w-0">
                                <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">
                                    Aperçu — {SCENARIOS.find(s => s.key === previewScenario)?.label} · Scope : {currentScope?.label}
                                </div>
                                <div className="text-xs text-slate-500 italic">
                                    Données factices : {SCENARIOS.find(s => s.key === previewScenario)?.hint}
                                </div>
                            </div>
                            <button
                                onClick={() => setPreviewOpen(false)}
                                className="p-1.5 rounded-full hover:bg-slate-200 transition"
                                aria-label="Fermer"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                        <iframe
                            title="Aperçu du contrat"
                            srcDoc={previewHtml}
                            className="flex-1 w-full border-0"
                            data-testid="contract-template-preview-iframe"
                        />
                    </div>
                </div>
            )}

            {/* Reset confirm */}
            {showResetConfirm && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
                    onClick={() => !resetting && setShowResetConfirm(false)}
                    data-testid="contract-template-reset-modal"
                >
                    <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden" onClick={(e) => e.stopPropagation()}>
                        <div className="bg-amber-500 text-white px-5 py-3 flex items-center gap-3">
                            <AlertTriangle className="w-5 h-5" />
                            <div className="font-black">Supprimer la personnalisation ?</div>
                        </div>
                        <div className="px-5 py-4 text-sm space-y-2">
                            <p>La version personnalisée <strong>v{meta?.version}</strong> et son <strong>historique complet</strong> pour « {currentScope?.label} » seront supprimés.</p>
                            <p className="text-slate-500 text-xs">
                                {scope === "master"
                                    ? "Le modèle d'usine (fichier livré avec l'app) redevient actif."
                                    : "Ce rôle utilisera à nouveau le modèle maître (s'il est personnalisé) ou le modèle d'usine."}
                            </p>
                        </div>
                        <div className="px-5 py-3 bg-slate-50 flex justify-end gap-2">
                            <button
                                onClick={() => setShowResetConfirm(false)}
                                disabled={resetting}
                                className="btn-shariti-outline text-xs"
                            >
                                Annuler
                            </button>
                            <button
                                onClick={handleReset}
                                disabled={resetting}
                                className="btn-shariti text-xs disabled:opacity-40"
                                data-testid="contract-template-reset-confirm"
                            >
                                {resetting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                                Confirmer
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Rollback confirm */}
            {rollbackTarget && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
                    onClick={() => !rollbackBusy && setRollbackTarget(null)}
                    data-testid="contract-template-rollback-modal"
                >
                    <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden" onClick={(e) => e.stopPropagation()}>
                        <div className="bg-shariti-orange text-white px-5 py-3 flex items-center gap-3">
                            <RefreshCw className="w-5 h-5" />
                            <div className="font-black">Restaurer la version {rollbackTarget.version} ?</div>
                        </div>
                        <div className="px-5 py-4 text-sm space-y-2">
                            <p>La version <strong>v{rollbackTarget.version}</strong> ({formatDateTime(rollbackTarget.updated_at)} par {rollbackTarget.updated_by_name || "—"}) sera restaurée comme <strong>version actuelle</strong>.</p>
                            <p className="text-slate-500 text-xs">Une <strong>nouvelle version v{(meta?.version || 0) + 1}</strong> sera créée avec ce contenu — la version actuelle est archivée dans l&apos;historique. Rien n&apos;est perdu.</p>
                        </div>
                        <div className="px-5 py-3 bg-slate-50 flex justify-end gap-2">
                            <button
                                onClick={() => setRollbackTarget(null)}
                                disabled={rollbackBusy}
                                className="btn-shariti-outline text-xs"
                            >
                                Annuler
                            </button>
                            <button
                                onClick={() => handleRollback(rollbackTarget.version)}
                                disabled={rollbackBusy}
                                className="btn-shariti text-xs disabled:opacity-40"
                                data-testid="contract-template-rollback-confirm"
                            >
                                {rollbackBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                                Restaurer
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {/* Diff modal — red/green line-by-line diff between a
                historical version and the current one. Purpose: audit
                trail — spot exactly what changed since a given date. */}
            {diffTarget && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
                    onClick={() => setDiffTarget(null)}
                    data-testid="contract-template-diff-modal"
                >
                    <div
                        className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl h-[85vh] flex flex-col overflow-hidden"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="px-5 py-3 border-b border-shariti-border flex items-center justify-between bg-shariti-cream flex-wrap gap-2">
                            <div className="min-w-0">
                                <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 inline-flex items-center gap-1.5">
                                    <GitCompareArrows className="w-3 h-3" /> Diff — {currentScope?.label}
                                </div>
                                <div className="text-xs text-shariti-black">
                                    <strong>v{diffTarget}</strong>
                                    <span className="text-slate-400 mx-1">→</span>
                                    <strong>v{meta?.version} (actuelle)</strong>
                                </div>
                                {diffData?.stats && (
                                    <div className="text-[11px] text-slate-600 mt-0.5">
                                        <span className="text-emerald-700 font-black">+{diffData.stats.added}</span>
                                        <span className="mx-1 text-slate-400">·</span>
                                        <span className="text-red-700 font-black">−{diffData.stats.removed}</span>
                                        <span className="mx-1 text-slate-400">·</span>
                                        <span className="text-slate-500">{diffData.stats.unchanged} lignes inchangées</span>
                                        {diffData.identical && (
                                            <span className="ml-2 px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 text-[10px] font-black uppercase">
                                                Contenu identique
                                            </span>
                                        )}
                                    </div>
                                )}
                            </div>
                            <button
                                onClick={() => setDiffTarget(null)}
                                className="p-1.5 rounded-full hover:bg-slate-200 transition"
                                aria-label="Fermer"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                        <div className="flex-1 overflow-auto bg-white">
                            {diffLoading ? (
                                <div className="h-full flex items-center justify-center">
                                    <Loader2 className="w-6 h-6 animate-spin text-shariti-orange" />
                                </div>
                            ) : diffData ? (
                                diffData.identical ? (
                                    <div className="p-8 text-center text-slate-500">
                                        <CheckCircle2 className="w-10 h-10 mx-auto text-emerald-500 mb-2" />
                                        <div className="text-sm">
                                            v{diffData.from.version} et v{diffData.to.version} ont un contenu <strong>identique</strong>.
                                        </div>
                                        <div className="text-xs mt-1 text-slate-400">
                                            La différence est probablement uniquement dans les métadonnées (auteur, date).
                                        </div>
                                    </div>
                                ) : (
                                    <div
                                        className="font-mono text-[11px] leading-snug"
                                        data-testid="contract-template-diff-body"
                                    >
                                        {diffData.chunks.map((chunk, idx) => {
                                            if (chunk.type === "collapsed") {
                                                return (
                                                    <div
                                                        key={idx}
                                                        className="bg-slate-50 border-y border-slate-200 py-1 text-center text-slate-400 text-[10px] italic"
                                                        data-testid="diff-collapsed"
                                                    >
                                                        ⋯ {chunk.count} lignes identiques masquées ⋯
                                                    </div>
                                                );
                                            }
                                            if (chunk.type === "equal") {
                                                return chunk.lines.map((ln, i) => (
                                                    <DiffLine
                                                        key={`${idx}-${i}`}
                                                        type="equal"
                                                        fromLine={chunk.from_start + i}
                                                        toLine={chunk.to_start + i}
                                                        line={ln}
                                                    />
                                                ));
                                            }
                                            if (chunk.type === "removed") {
                                                return chunk.lines.map((ln, i) => (
                                                    <DiffLine
                                                        key={`${idx}-${i}`}
                                                        type="removed"
                                                        fromLine={chunk.from_start + i}
                                                        line={ln}
                                                    />
                                                ));
                                            }
                                            if (chunk.type === "added") {
                                                return chunk.lines.map((ln, i) => (
                                                    <DiffLine
                                                        key={`${idx}-${i}`}
                                                        type="added"
                                                        toLine={chunk.to_start + i}
                                                        line={ln}
                                                    />
                                                ));
                                            }
                                            return null;
                                        })}
                                    </div>
                                )
                            ) : (
                                <div className="p-8 text-center text-slate-500 text-sm">
                                    Impossible de charger le diff.
                                </div>
                            )}
                        </div>
                        {diffData && !diffData.identical && (
                            <div className="px-5 py-3 border-t border-shariti-border bg-slate-50 flex items-center justify-between gap-2 flex-wrap">
                                <div className="text-[11px] text-slate-500">
                                    Les <strong className="text-red-700">lignes rouges</strong> ont été supprimées ·
                                    Les <strong className="text-emerald-700">lignes vertes</strong> ont été ajoutées ·
                                    Utile pour audit URSSAF / contrôle interne.
                                </div>
                                <button
                                    onClick={() => { setDiffTarget(null); setRollbackTarget(meta.history.find(h => h.version === diffTarget)); }}
                                    className="btn-shariti-outline text-xs"
                                    data-testid="contract-template-diff-rollback-shortcut"
                                >
                                    <RefreshCw className="w-3.5 h-3.5" />
                                    Restaurer v{diffTarget}
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}


/** Single-line diff row used inside the diff modal. Green for added,
 * red for removed, neutral for context. Line numbers are shown on both
 * sides so the admin can locate the change in the raw source. */
function DiffLine({ type, fromLine, toLine, line }) {
    const bg = type === "added" ? "bg-emerald-50" : type === "removed" ? "bg-red-50" : "bg-white";
    const marker = type === "added" ? "+" : type === "removed" ? "−" : " ";
    const markerCls = type === "added" ? "text-emerald-700" : type === "removed" ? "text-red-700" : "text-slate-300";
    const textCls = type === "added" ? "text-emerald-900" : type === "removed" ? "text-red-900" : "text-slate-700";
    return (
        <div className={`grid grid-cols-[42px_42px_18px_1fr] gap-1 ${bg} border-b border-slate-100 hover:bg-opacity-70`}>
            <div className="text-right pr-1 text-slate-300 select-none tabular-nums text-[10px]">
                {fromLine != null ? fromLine : ""}
            </div>
            <div className="text-right pr-1 text-slate-300 select-none tabular-nums text-[10px]">
                {toLine != null ? toLine : ""}
            </div>
            <div className={`text-center font-black ${markerCls} select-none`}>{marker}</div>
            <pre className={`whitespace-pre-wrap break-words py-0.5 pr-2 ${textCls}`}>{line || " "}</pre>
        </div>
    );
}
